import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, X, Check, Loader2,
  HeartHandshake, Brain, ShieldAlert, Trophy,
  ClipboardCheck, Coins, Users as UsersIcon, Scale, BookOpen, CalendarDays, Briefcase, GraduationCap,
} from "lucide-react";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { setUserRole } from "@/lib/userRole";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const B2C_ROLES = new Set(["pflegekraft", "angehoerig"]);
const B2B_ROLES = new Set(["pdl", "leitung", "qm", "inhaber", "abrechnung"]);

const PAIN_B2C = [
  { id: "pflegegrad", icon: Scale, title: "Pflegegrad-Streit", desc: "MDK stuft falsch ein – ich brauche eine fertige Strategie.",
    rec: { vault: "pflegegrad-hoeherstufung-spielbuch", to: "/leitfaeden/pflegekraft-bundle", label: "Pflegegrad-Spielbuch + Demenz-Sätze + Sturz-Akutleitfaden" } },
  { id: "demenz", icon: Brain, title: "Demenz-Patient:in", desc: "Schwierige Kommunikation, Eskalation, Angehörige genervt.",
    rec: { vault: "demenz-25-saetze", to: "/leitfaeden/pflegekraft-bundle", label: "25 Demenz-Sätze + 3 weitere PDFs" } },
  { id: "sturz", icon: ShieldAlert, title: "Sturz-Akutfall", desc: "Was tun in den ersten 60 Minuten? Doku, Angehörige, Recht.",
    rec: { vault: "sturz-bewohner-leitfaden", to: "/leitfaeden/pflegekraft-bundle", label: "Sturz-Akutleitfaden + Bundle" } },
  { id: "karriere", icon: Trophy, title: "Karriere-Sprung", desc: "Weiterbildung, Bildungsgutschein, Spezialisierung.",
    rec: { vault: null, to: "/pflegekraefte", label: "Kurse mit Bildungsgutschein" } },
];

const PAIN_B2B = [
  { id: "md-pruefung", icon: ClipboardCheck, title: "MD-Prüfung steht an", desc: "13 Mängel im letzten Bericht – nie wieder.",
    rec: { vault: "md-pruefung-7-tage-plan", to: "/leitfaeden/md-schutzschild", label: "MD-Schutzschild-Bundle: 7-Tage-Plan + Survival-Guide" } },
  { id: "kuerzungen", icon: Coins, title: "Pflegekassen-Kürzungen", desc: "Wieder zu wenig genehmigt – ich brauche Musterwidersprüche.",
    rec: { vault: "kuerzungen-widerspruch", to: "/leitfaeden/cash-bundle", label: "Cash-Bundle: Abrechnungs-Atlas + 12 Musterwidersprüche" } },
  { id: "personalmangel", icon: UsersIcon, title: "Personalmangel", desc: "Stellen unbesetzt, Recruiting kostet zu viel.",
    rec: { vault: null, to: "/campus/app/jobs", label: "Stelle ausschreiben · Bewerber:innen-Pool" } },
  { id: "pflegesatz", icon: Scale, title: "Pflegesatz-Verhandlung", desc: "Nächste Verhandlung im Anflug – wie argumentiere ich?",
    rec: { vault: "pflegesatz-verhandlungs-matrix", to: "/campus/app/schatzkiste", label: "Pflegesatz-Verhandlungs-Matrix" } },
];

const INTERESTS = [
  { id: "vorlagen", icon: BookOpen, title: "Vorlagen & PDFs", desc: "Fertige Templates für Doku, Briefe, Checklisten." },
  { id: "briefings", icon: CalendarDays, title: "Live-Briefings", desc: "Monatlich 60 Min Zoom zu Reform, MDK & Recht." },
  { id: "community", icon: HeartHandshake, title: "Community", desc: "#mdk-lagezentrum: Fragen stellen, Antworten kriegen." },
  { id: "jobs", icon: Briefcase, title: "Jobs & Talente", desc: "Stellen finden oder Personal entdecken." },
  { id: "ausbildung", icon: GraduationCap, title: "Weiterbildung", desc: "Kurse mit Bildungsgutschein, jeden Montag Start." },
];

function audienceFromRole(role) {
  if (!role) return null;
  if (B2C_ROLES.has(role)) return "b2c";
  if (B2B_ROLES.has(role)) return "b2b";
  return null;
}

export default function OnboardingWizard() {
  const { user, refresh } = useCampusAuth();
  const navigate = useNavigate();
  const initialAudience = useMemo(() => audienceFromRole(user?.role), [user]);
  const [audience, setAudience] = useState(initialAudience);
  const [pain, setPain] = useState(null);
  const [interest, setInterest] = useState(null);
  const [step, setStep] = useState(initialAudience ? 1 : 0);
  const [saving, setSaving] = useState(false);

  // Only show when user is logged in and has not finished or skipped onboarding.
  if (!user) return null;
  const ob = user.onboarding || {};
  if (ob.completed === true || ob.skipped_at) return null;

  const painOptions = audience === "b2c" ? PAIN_B2C : audience === "b2b" ? PAIN_B2B : [];
  const selectedPain = painOptions.find((p) => p.id === pain);
  const totalSteps = 4; // 0 audience · 1 pain · 2 interest · 3 done

  const handleSkip = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/skip`, {}, { withCredentials: true });
      await refresh?.();
      toast.success("Alles klar – du kannst die Tour später jederzeit erneut starten.");
    } catch {
      toast.error("Konnte nicht gespeichert werden.");
    }
    setSaving(false);
  };

  const handleFinish = async () => {
    if (!audience || !pain) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/onboarding`,
        { audience, pain_point: pain, primary_interest: interest },
        { withCredentials: true }
      );
      // Memo role for context-aware site components.
      if (user?.role) setUserRole(user.role);
      await refresh?.();
      toast.success("Stark – wir richten deinen Campus passgenau aus.");
      if (selectedPain?.rec?.to) {
        // Tiny delay so toast is visible before navigation.
        setTimeout(() => navigate(selectedPain.rec.to), 350);
      }
    } catch {
      toast.error("Speichern fehlgeschlagen – bitte erneut versuchen.");
    }
    setSaving(false);
  };

  return (
    <div
      data-testid="onboarding-wizard"
      className="fixed inset-0 z-[80] bg-sand-900/55 backdrop-blur-[2px] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full sm:max-w-[640px] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-brand-darkred text-white px-7 sm:px-9 pt-7 pb-6 relative">
          <button
            onClick={handleSkip}
            disabled={saving}
            data-testid="onboarding-skip"
            className="absolute top-4 right-4 text-cream-100/70 hover:text-white text-[12px] uppercase tracking-[0.22em] font-semibold inline-flex items-center gap-1.5"
          >
            Überspringen <X className="h-3.5 w-3.5" />
          </button>
          <div className="text-[10.5px] uppercase tracking-[0.3em] font-bold text-brand-rose mb-3 inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Willkommens-Tour · 60 Sekunden
          </div>
          <h2 className="font-display text-[1.6rem] sm:text-[1.9rem] leading-[1.15]">
            Schön, dass du hier bist, <span className="italic text-brand-rose">{user?.name?.split(" ")[0] || "Held:in"}.</span>
          </h2>
          <p className="mt-3 text-cream-100/85 text-[14px] leading-relaxed max-w-md">
            Drei kurze Klicks – und dein Campus zeigt dir genau das, was du gerade brauchst.
          </p>
          {/* Step indicator */}
          <div className="mt-5 flex items-center gap-1.5">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${i === step ? "w-8 bg-brand-rose" : i < step ? "w-4 bg-brand-rose/70" : "w-4 bg-white/25"}`}
              />
            ))}
            <span className="ml-2 text-[11px] text-cream-100/70">Schritt {step + 1} / {totalSteps}</span>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 sm:px-9 py-7 max-h-[60vh] sm:max-h-[55vh] overflow-y-auto">
          {step === 0 && (
            <Step title="Bist du Pflegekraft oder leitest du eine Einrichtung?" desc="Wir blenden danach nur das ein, was für dich Sinn ergibt.">
              <div className="grid sm:grid-cols-2 gap-3">
                <ChoiceCard
                  testId="onboarding-audience-b2c"
                  selected={audience === "b2c"}
                  onClick={() => { setAudience("b2c"); setStep(1); }}
                  icon={HeartHandshake}
                  title="Ich pflege / arbeite in der Pflege"
                  desc="Pflegekraft, Angehörige:r, frisch ausgebildet."
                />
                <ChoiceCard
                  testId="onboarding-audience-b2b"
                  selected={audience === "b2b"}
                  onClick={() => { setAudience("b2b"); setStep(1); }}
                  icon={Briefcase}
                  title="Ich leite eine Einrichtung"
                  desc="PDL, QM, Inhaber:in, Abrechnung."
                />
              </div>
            </Step>
          )}

          {step === 1 && (
            <Step
              title={audience === "b2c" ? "Was ist gerade dein Top-Thema?" : "Wo brennt's bei dir aktuell am meisten?"}
              desc="Aussuchen – wir packen deinen ersten Quick-Win direkt obendrauf."
            >
              <div className="grid sm:grid-cols-2 gap-3">
                {painOptions.map((p) => (
                  <ChoiceCard
                    key={p.id}
                    testId={`onboarding-pain-${p.id}`}
                    selected={pain === p.id}
                    onClick={() => setPain(p.id)}
                    icon={p.icon}
                    title={p.title}
                    desc={p.desc}
                  />
                ))}
              </div>
            </Step>
          )}

          {step === 2 && (
            <Step
              title="Was möchtest du als Nächstes erkunden?"
              desc="Wir setzen dir einen ersten Pin – kannst du jederzeit anpassen."
            >
              <div className="grid sm:grid-cols-2 gap-3">
                {INTERESTS.map((p) => (
                  <ChoiceCard
                    key={p.id}
                    testId={`onboarding-interest-${p.id}`}
                    selected={interest === p.id}
                    onClick={() => setInterest(p.id)}
                    icon={p.icon}
                    title={p.title}
                    desc={p.desc}
                  />
                ))}
              </div>
            </Step>
          )}

          {step === 3 && (
            <Step
              title="Fertig – dein erster Quick-Win wartet."
              desc="Wir leiten dich gleich genau dort hin, wo du loslegen kannst."
            >
              <div className="rounded-2xl bg-cream-50 border border-cream-300 p-5">
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra mb-2">
                  Dein Quick-Win
                </div>
                <div className="font-display text-sand-800 text-[1.2rem] leading-snug">
                  {selectedPain?.rec?.label || "Deine Empfehlung steht im Dashboard bereit."}
                </div>
                <div className="mt-2 text-[13px] text-sand-600">
                  Du landest danach automatisch dort – und kannst alle anderen Werkzeuge jederzeit im Menü links nachholen.
                </div>
              </div>
            </Step>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 sm:px-9 py-5 border-t border-cream-300 bg-cream-50 flex items-center justify-between gap-4">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || saving}
            data-testid="onboarding-back"
            className="text-[13px] text-sand-600 hover:text-sand-800 disabled:opacity-30"
          >
            Zurück
          </button>
          {step < 3 && (
            <button
              onClick={() => setStep((s) => Math.min(3, s + 1))}
              disabled={
                saving ||
                (step === 0 && !audience) ||
                (step === 1 && !pain) ||
                (step === 2 && !interest)
              }
              data-testid="onboarding-next"
              className="inline-flex items-center gap-2 rounded-full bg-sand-800 hover:bg-sand-700 disabled:bg-sand-300 disabled:cursor-not-allowed text-white h-11 px-6 text-[13.5px] font-semibold transition-colors"
            >
              Weiter <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </button>
          )}
          {step === 3 && (
            <button
              onClick={handleFinish}
              disabled={saving}
              data-testid="onboarding-finish"
              className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white h-11 px-6 text-[13.5px] font-semibold transition-colors"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" strokeWidth={2.4} />}
              Los geht&apos;s
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ title, desc, children }) {
  return (
    <div>
      <h3 className="font-display text-sand-800 text-[1.35rem] leading-snug">{title}</h3>
      {desc && <p className="mt-1.5 text-sand-600 text-[13.5px] leading-relaxed">{desc}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

function ChoiceCard({ selected, onClick, icon: Icon, title, desc, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-xl border p-4 transition-all ${
        selected
          ? "border-terra bg-peach-50 ring-2 ring-terra/30"
          : "border-cream-300 hover:border-sand-400 bg-white"
      }`}
    >
      <Icon className={`h-5 w-5 ${selected ? "text-terra" : "text-sand-700"}`} strokeWidth={1.7} />
      <div className="mt-3 font-display text-sand-800 text-[1.02rem] font-semibold leading-tight">
        {title}
      </div>
      <div className="mt-1 text-[12.5px] text-sand-600 leading-snug">{desc}</div>
    </button>
  );
}
